import rootStore from '@vromo/dashboard/src/stores/RootStore';

import { Parser } from 'json2csv';

import format from 'date-fns/format';

import flatMap from 'lodash/flatMap';
import last from 'lodash/last';
import capitalize from 'lodash/capitalize';
import range from 'lodash/range';
import max from 'lodash/max';
import uniq from 'lodash/uniq';

import { CancelType } from '@vromo/models';
import { Job, issueReasons } from '@vromo/models';
import convertDistance from '@vromo/utils/convertDistance';
import getReadableFromDateRange from './getReadableFromDateRange';
import { findLast } from 'lodash';

const ratingScale = 5;

export type Column<T> = {
    label: string;
    value: (job: T) => string | number | undefined | null;
    default: string;
};

export default function createCSVForJobs(jobs: Job[]): string {
    const { viewStore, groupStore, vehicleStore, policyStore, siteStore, campaignStore } =
        rootStore;

    const attributes = uniq(flatMap(jobs.map((j) => j.attr?.items.map((a) => a.key))));
    const maxTasks = max(jobs.map((j) => j.tasks?.items?.length));
    const maxNotes = max(jobs.map((j) => j.notes?.items?.length));
    const maxIssues = max(jobs.map((j) => j.issues?.items?.length));

    const hasExternalId = jobs.some((j) => j.external != undefined);

    const distUnits = viewStore.imperialUnits ? 'mi' : 'km';

    const parserOptions: {
        fields: Column<Job>[];
    } = {
        fields: [
            {
                label: 'Job ID',
                value: (job) => job.id,
                default: ''
            },
            ...(hasExternalId
                ? [
                      {
                          label: 'External ID',
                          value: (job: Job) => job.external,
                          default: ''
                      }
                  ]
                : []),
            {
                label: 'Group',
                value: (job) => (job.group ? groupStore.withId(job.group)?.name : null),
                default: ''
            },
            {
                label: 'Order Name',
                value: (job) => job.name,
                default: ''
            },
            {
                label: 'Status',
                value: (job) => capitalize(job.status),
                default: ''
            },
            {
                label: 'Cancel Reason',
                value: (job) => job.cancelled?.reason && CancelType[job.cancelled.reason.id!],
                default: ''
            },
            {
                label: 'Cancel Note',
                value: (job) => job.cancelled?.note,
                default: ''
            },
            {
                label: 'Vehicle ID',
                value: (job) => last(job.dispatches?.items)?.vehicle?.id,
                default: ''
            },
            {
                label: 'Driver',
                value: (job) => {
                    const lastVehicle = last(job.dispatches?.items)?.vehicle;
                    return lastVehicle?.driver?.contact?.name ?? null;
                },
                default: ''
            },
            {
                label: 'Driver ID',
                value: (job) => {
                    const lastVehicle = last(job.dispatches?.items)?.vehicle;
                    return lastVehicle?.driver?.id;
                },
                default: ''
            },
            {
                label: 'External Id',
                value: (job) => {
                    const lastVehicle = last(job.dispatches?.items)?.vehicle?.id;
                    return lastVehicle ? vehicleStore.withId(lastVehicle)?.external : null;
                },
                default: ''
            },
            {
                label: 'Contact Email',
                value: (job) => job.contact?.email,
                default: ''
            },
            {
                label: 'Contact Name',
                value: (job) => job.contact?.name,
                default: ''
            },
            {
                label: 'Contact Phone',
                value: (job) => job.contact?.phone,
                default: ''
            },
            // {
            //     label: 'SMS Opt-in',
            //     value: (job) => (job?.optedInForCampaignSms ? 'Yes' : 'No'),
            //     default: ''
            // },
            {
                label: 'Job Creation Time',
                value: (job) => (job.created ? format(job.created, 'YYYY-MM-DD HH:mm:ss') : null),
                default: ''
            },
            {
                label: 'Job Assigned Time',
                value: (job) => {
                    const assignedTime = findLast(
                        job.dispatches?.items,
                        (it) => it.type === 'dispatched'
                    )?.created;
                    return assignedTime ? format(assignedTime, 'YYYY-MM-DD HH:mm:ss') : null;
                },
                default: ''
            },

            {
                label: 'Job Start Time',
                value: (job) => {
                    const underwayTime = job.dispatches?.items?.find(
                        (it) => it.type === 'underway'
                    )?.created;
                    return underwayTime ? format(underwayTime, 'YYYY-MM-DD HH:mm:ss') : null;
                },
                default: ''
            },

            // TASKS
            ...range(0, maxTasks).reduce<Column<Job>[]>((taskCols, ix) => {
                taskCols.push(
                    {
                        label: `Task ${ix + 1} Name`,
                        value: (job) => job.tasks?.items?.[ix]?.zone?.name,
                        default: ''
                    },
                    {
                        label: `Task ${ix + 1} Address`,
                        value: (job) => job.tasks?.items?.[ix]?.zone?.address,
                        default: ''
                    },
                    {
                        label: `Task ${ix + 1} Latitude`,
                        value: (job) => job.tasks?.items?.[ix]?.zone?.coords?.lat,
                        default: ''
                    },
                    {
                        label: `Task ${ix + 1} Longitude`,
                        value: (job) => job.tasks?.items?.[ix]?.zone?.coords?.lon,
                        default: ''
                    },
                    {
                        label: `Task ${ix + 1} Deadline`,
                        value: (job) => {
                            const deadline = job.tasks?.items?.[ix]?.timeframe?.by;

                            return deadline ? format(deadline, 'YYYY-MM-DD HH:mm:ss') : null;
                        },

                        default: ''
                    },
                    {
                        label: `Task ${ix + 1} Completion Time`,
                        value: (job) => {
                            const completed = job.tasks?.items?.[ix]?.completed?.time;

                            return completed ? format(completed, 'YYYY-MM-DD HH:mm:ss') : null;
                        },
                        default: ''
                    },
                    {
                        label: `Task ${ix + 1} Lateness`,
                        value: (job) =>
                            getReadableFromDateRange(
                                job.tasks.items[ix]?.timeframe?.by,
                                job.tasks.items[ix]?.completed?.time
                            ),
                        default: ''
                    }
                );

                return taskCols;
            }, []),

            ...range(0, maxIssues).reduce<Column<Job>[]>((issueCols, ix) => {
                issueCols.push(
                    {
                        label: `Issue ${ix + 1}`,
                        value: (job) => {
                            const issue = job.issues?.items?.[ix];
                            return issue ? issue.type && issueReasons[issue.type] : null;
                        },
                        default: ''
                    },
                    {
                        label: `Issue ${ix + 1} Created`,
                        value: (job) => {
                            const issue = job.issues?.items?.[ix];
                            return issue ? format(issue.created, 'YYYY-MM-DD HH:mm:ss') : null;
                        },
                        default: ''
                    }
                );
                return issueCols;
            }, []),

            {
                label: 'Job Completion Time',
                value: (job) => {
                    const completed = job.finished;
                    return completed ? format(completed, 'YYYY-MM-DD HH:mm:ss') : null;
                },
                default: ''
            },

            {
                label: `Off Job Distance (${distUnits})`,
                value: (job) => {
                    const offJobDist = job.route?.items.find((r) => r.ix === 0)?.distance;
                    return offJobDist
                        ? convertDistance(offJobDist, viewStore.imperialUnits).distance().toFixed(2)
                        : null;
                },
                default: ''
            },
            {
                label: `On Job Distance (${distUnits})`,
                value: (job) => {
                    const onJobDist = job.route?.items
                        .filter((r) => r.ix !== 0)
                        .reduce((dist, r) => {
                            const routeLegDistance = r.distance || 0;
                            return dist + routeLegDistance;
                        }, 0);
                    return onJobDist
                        ? convertDistance(onJobDist, viewStore.imperialUnits).distance().toFixed(2)
                        : null;
                },
                default: ''
            },
            {
                label: `Total Distance (${distUnits})`,
                value: (job) => {
                    const totalDist = job.route?.items.reduce((dist, r) => {
                        const routeLegDistance = r.distance || 0;
                        return dist + routeLegDistance;
                    }, 0);
                    return totalDist
                        ? convertDistance(totalDist, viewStore.imperialUnits).distance().toFixed(2)
                        : null;
                },
                default: ''
            },
            {
                label: 'Rating (/5)',
                value: (job) =>
                    job.ratings?.items?.length
                        ? (job.ratings.items[0]!.ratio * ratingScale).toFixed(1)
                        : null,
                default: ''
            },
            {
                label: 'Rating Comment',
                value: (job) => (job.ratings?.items?.length ? job.ratings.items[0]?.comment : null),
                default: ''
            },
            {
                label: 'Auto Dispatcher ID',
                value: (job) => job.policy?.id,
                default: ''
            },
            {
                label: 'Auto Dispatcher Name',
                value: (job) => (job.policy?.id ? policyStore.withId(job.policy.id)?.name : null),
                default: ''
            },
            {
                label: 'Campaign ID',
                value: (job) => job.campaign?.id,
                default: ''
            },
            {
                label: 'Campaign Name',
                value: (job) =>
                    job.campaign?.id ? campaignStore.withId(job.campaign.id)?.name : null,

                default: ''
            },
            {
                label: 'Site ID',
                value: (job) => job.site?.id,
                default: ''
            },
            {
                label: 'Site Name',
                value: (job) =>
                    job.site?.id ? siteStore.withId(job.site.id)?.contact?.name : null,
                default: ''
            },
            {
                label: 'Tracking Link Clicks',
                value: (job) =>
                    Object.values(job.stages).reduce<number>((total, s) => {
                        const clicks = s.analytics?.clicksTotal;
                        return clicks ? total + clicks : total;
                    }, 0),
                default: ''
            },

            // DRIVER NOTES
            ...range(0, maxNotes).reduce<Column<Job>[]>((noteCols, ix) => {
                noteCols.push({
                    label: `Driver Note ${ix + 1}`,
                    value: (job) => job.notes?.items?.[ix]?.contents,
                    default: ''
                });

                return noteCols;
            }, []),

            // JOB ATTRIBUTES
            ...(attributes.map((key) => ({
                label: key,
                value: (job: Job) => job.attr?.items?.find((a) => a.key === key)?.value,
                default: ''
            })) as Column<Job>[])
        ]
    };

    const parser = new Parser(parserOptions);

    const contents = parser.parse(jobs);

    return window.URL.createObjectURL(new Blob([contents]));
}
